import React, {useCallback, useState} from 'react';

import {DateInput, FormInput} from 'src/components/input';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {apiV2ReleaseNotesDescription} from './release-notes.module.styl';

import type {ReleaseNote} from 'src/openapi/admin/models/ReleaseNote';


type Props = {
    onSave?: (releaseNote: ReleaseNote) => void,
    onUpdate?: (releaseNote: ReleaseNote) => void,
    onCancel?: (id: number) => void,
    id?: number,
    version?: string,
    releaseDate?: string,
    scope?: string,
    details?: string,
    canEdit?: boolean,
};

export const ReleaseNoteInput = (props: Props) => {
    const {
        onSave, onCancel, onUpdate, id, version: noteVersion, releaseDate: noteReleaseDate, scope: noteScope,
        details: noteDetails, canEdit,
    } = props;

    const [version, setVersion] = useState(noteVersion || '');
    const [releaseDate, setReleaseDate] = useState(noteReleaseDate || '');
    const [scope, setScope] = useState(noteScope || '');
    const [details, setDetails] = useState(noteDetails || '');

    const changeDetails = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDetails(e.target.value);
    }, []);

    const blurDetails = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
        setDetails(e.currentTarget.value.trim());
    }, []);

    const resetInput = useCallback(() => {
        setVersion('');
        setReleaseDate('');
        setScope('');
        setDetails('');
    }, []);

    const onReleaseNoteSave = useCallback(() => {
        if (onUpdate) {
            onUpdate({version, releaseDate, scope, details, id});
        }
        if (onSave) {
            onSave({version, releaseDate, scope, details});
            resetInput();
        }

    }, [version, releaseDate, scope, details, onSave, onUpdate, id, resetInput]);

    const onEditCancel = useCallback(() => {
        if (onCancel && id) {
            onCancel(id);
        }
    }, [onCancel, id]);

    const isReadyToAdd = version && releaseDate && scope && details;
    const isChanged = version !== noteVersion
        || releaseDate !== noteReleaseDate
        || scope !== noteScope
        || details !== noteDetails;

    return (
        <Group direction="v">
            <Group alignItems="start">
                <Item shrink={0} style={{width: 100}}>
                    <FormInput
                        value={version}
                        placeholder="Version"
                        maxLength={10}
                        readOnly={!canEdit}
                        onBlur={setVersion}
                    />
                </Item>
                <Item shrink={0} style={{width: 150}}>
                    <DateInput
                        {...{
                            id: 'release-date',
                            selectedDate: releaseDate,
                            clearable: true,
                            readonly: !canEdit,
                            onChange: setReleaseDate,
                            isLocalDate: false,
                        }}
                    />
                </Item>
                <Item shrink={0} style={{width: 100}}>
                    <FormInput
                        value={scope}
                        placeholder="Scope"
                        onBlur={setScope}
                        readOnly={!canEdit}
                        width={100}
                    />
                </Item>
                <Item grow={1} className={apiV2ReleaseNotesDescription}>
                    <Textarea
                        maxLength={3000}
                        resizable={false}
                        placeholder="Details"
                        value={details}
                        disabled={!canEdit}
                        onChange={changeDetails}
                        onBlur={blurDetails}
                    />
                </Item>
            </Group>
            <Group>
                <Button
                    disabled={!isReadyToAdd || (id ? !isChanged : false) || !canEdit}
                    variant="Type.TERTIARY"
                    onClick={onReleaseNoteSave}>
                    {id ? 'Save' : 'Add'}
                </Button>
                {id ? (
                    <Button variant="Type.TERTIARY" onClick={onEditCancel} disabled={!canEdit}>
                        Cancel
                    </Button>
                ) : null}
            </Group>
        </Group>
    );
};
